import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Loader2, Save, Shield, MapPin, User, Mail } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  useRolesQuery,
  useUpdateUserRoleMutation,
  useUpdateUserFranchisesMutation
} from '@/features/users/hooks'
import { FRANCHISE_REGIONS } from '@/features/users/utils/franchiseRegions'
import type { UserListItem } from '@/features/users/types'

interface EditUserModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: Pick<UserListItem, 'id' | 'email' | 'full_name' | 'avatar_url' | 'role_name' | 'assigned_franchises'>
  onSuccess: () => void
}

export function EditUserModal({ open, onOpenChange, user, onSuccess }: EditUserModalProps) {
  const [selectedRoleId, setSelectedRoleId] = useState('')
  const [selectedFranchises, setSelectedFranchises] = useState<string[]>([])
  const [isDirty, setIsDirty] = useState(false)
  const { success: toastSuccess, error: toastError } = useToast()

  // Fetch available roles
  const { data: roles, isLoading: rolesLoading } = useRolesQuery()

  // Initialize form with user data
  useEffect(() => {
    if (user && roles) {
      const userRole = roles.find(r => r.name === user.role_name)
      setSelectedRoleId(userRole?.id || '')
      setSelectedFranchises(user.assigned_franchises || [])
      setIsDirty(false)
    }
  }, [user, roles])

  // Update role mutation
  const updateRoleMutation = useUpdateUserRoleMutation()

  // Update franchises mutation
  const updateFranchisesMutation = useUpdateUserFranchisesMutation()

  const handleFranchiseToggle = (franchise: string, checked: boolean) => {
    if (checked) {
      setSelectedFranchises(prev => [...prev, franchise])
    } else {
      setSelectedFranchises(prev => prev.filter(f => f !== franchise))
    }
    setIsDirty(true)
  }

  const handleRoleChange = (roleId: string) => {
    setSelectedRoleId(roleId)
    setIsDirty(true)
  }

  const handleSave = async () => {
    try {
      const currentRole = roles?.find(r => r.name === user.role_name)
      const newRole = roles?.find(r => r.id === selectedRoleId)

      // Update role if changed
      if (currentRole?.id !== selectedRoleId) {
        await updateRoleMutation.mutateAsync({
          userId: user.id,
          roleId: selectedRoleId
        })
      }

      // Update franchises if changed
      const franchisesChanged =
        selectedFranchises.length !== user.assigned_franchises.length ||
        selectedFranchises.some(f => !user.assigned_franchises.includes(f)) ||
        user.assigned_franchises.some(f => !selectedFranchises.includes(f))

      if (franchisesChanged) {
        await updateFranchisesMutation.mutateAsync({
          userId: user.id,
          franchiseRegions: selectedFranchises
        })
      }

      toastSuccess('User updated', 'User updated successfully')
      setIsDirty(false)
      onSuccess()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update user'
      toastError('Failed to update user', message)
    }
  }

  const selectedRole = roles?.find(r => r.id === selectedRoleId)
  const isAdminRole = selectedRole?.name === 'admin'
  const isLoading = updateRoleMutation.isPending || updateFranchisesMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Edit User
          </DialogTitle>
          <DialogDescription>
            Update user role and franchise assignments
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* User Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">User Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={user.avatar_url || ''} />
                  <AvatarFallback>
                    {user.full_name?.split(' ').map(n => n[0]).join('') ||
                     user.email.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-medium text-lg">{user.full_name || 'No name'}</div>
                  <div className="text-gray-600 flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    {user.email}
                  </div>
                  <Badge variant="outline" className="mt-1">
                    Current: {user.role_name}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Role Selection */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <Label>Role</Label>
            </div>
            <Select value={selectedRoleId} onValueChange={handleRoleChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {rolesLoading ? (
                  <div className="p-2 text-center">
                    <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                  </div>
                ) : (
                  roles?.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        <div>
                          <div className="font-medium">{role.name}</div>
                          <div className="text-xs text-gray-500">{role.description}</div>
                        </div>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>

            {selectedRole && (
              <Card className="bg-gray-50">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{selectedRole.name}</div>
                      <div className="text-sm text-gray-600">{selectedRole.description}</div>
                    </div>
                    <Badge variant={selectedRole.name === 'admin' ? 'destructive' : 'default'}>
                      Priority: {selectedRole.priority}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Franchise Access */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <Label>Franchise Access</Label>
            </div>

            {isAdminRole ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center text-gray-500">
                    <Shield className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                    <p className="font-medium">Admin users have access to all franchises</p>
                    <p className="text-sm">No franchise restrictions apply to admin roles</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  Select which franchise locations this user can access. If none are selected, they will have access to all franchises.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {FRANCHISE_REGIONS.map((franchise) => (
                    <div key={franchise.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-${franchise.value}`}
                        checked={selectedFranchises.includes(franchise.value)}
                        onCheckedChange={(checked) =>
                          handleFranchiseToggle(franchise.value, checked as boolean)
                        }
                      />
                      <Label htmlFor={`edit-${franchise.value}`} className="text-sm font-normal">
                        {franchise.label}
                      </Label>
                    </div>
                  ))}
                </div>
                {selectedFranchises.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-2">
                    {selectedFranchises.map((franchise) => {
                      const region = FRANCHISE_REGIONS.find(f => f.value === franchise)
                      return (
                        <Badge key={franchise} variant="outline">
                          {region?.label}
                        </Badge>
                      )
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Changes Summary */}
          {isDirty && (
            <Card className="border-yellow-200 bg-yellow-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-yellow-800">Pending Changes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-yellow-700">
                {roles && selectedRoleId !== roles.find(r => r.name === user.role_name)?.id && (
                  <div>• Role will be changed to <strong>{selectedRole?.name}</strong></div>
                )}
                {(selectedFranchises.length !== (user.assigned_franchises?.length || 0) ||
                  selectedFranchises.some(f => !(user.assigned_franchises || []).includes(f)) ||
                  (user.assigned_franchises || []).some(f => !selectedFranchises.includes(f))) && (
                  <div>
                    • Franchise access will be updated to{' '}
                    <strong>
                      {isAdminRole
                        ? 'all franchises (Admin)'
                        : selectedFranchises.length > 0
                          ? `${selectedFranchises.length} selected franchises`
                          : 'all franchises'
                      }
                    </strong>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isDirty || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
